import * as React from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import {
  useAuth,
  useSidePanel,
  useAuthApi,
  useCustomerApi,
  usePageNotifications,
  useCurrentAgency,
  useDynamicPageTitle,
  useLogger,
  useFormFactor,
  useViewportHeight,
} from 'hooks'
import { Pages as allPages, Login, NotFound, VerifyEmailAddress } from 'pages'
import { buildNavLink } from 'pages/support/CustomerDetails'
import {
  CssBaseline,
  AppBar,
  HeaderBar,
  Box,
  Snackbar,
  Button,
  Tooltip,
  Typography,
} from 'cs-kevlar-react'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { PageDefinition, PageType } from 'pages/pageDefinition'
import { AppBarHeight } from 'cs-kevlar-react/lib/es/theming/baseConstants'
import {
  SidePanel,
  SIDE_PANEL_CONTENT_IDS,
} from 'cs-kevlar-react/lib/es/SidePanel'
import clsx from 'clsx'
import { NavContainer } from 'components/navigation'
import {
  AssistBanner,
  ErrorBoundary,
  SessionExpirationWarning,
} from 'components'
import { removeUnauthorizedPages, doesPageMatchRoute } from 'utils/pages'
import { DEFAULT_SUPPORT_ROUTE, DEFAULT_AGENCY_ROUTE, DEFAULT_ADMIN_ROUTE } from 'utils'
import { PAGE_TITLE_RIGHT_ACTIONS_ID } from 'components/PageTitleRightActions'
import { PAGE_TITLE_CENTER_ACTIONS_ID } from 'components/PageTitleCenterActions'
import { PAGE_TITLE_SUBTITLE_ID } from 'components/PageTitleSubtitle'
import { Version, WebAdminUser } from './api'
import { CurrentCustomerContext, PageContext } from 'contexts'
import MuiMenuIcon from '@mui/icons-material/Menu'
import MuiCloseIcon from '@mui/icons-material/Close'
import { ExitIcon } from 'cs-kevlar-react/lib/es/icons'
import { IconButton } from 'cs-kevlar-react'
import { useStatusApi } from 'hooks/useStatusApi'

const SidePanelWidth = 400

const determineAppBarAndAssistBannerHeights = (user: WebAdminUser | null) => {
  if (user !== null && user.isAssisting && user.isSupport)
    return 40 + AppBarHeight

  return AppBarHeight
}

interface StyleProps {
  currentTitle: string | null
  user: WebAdminUser | null
  isRenderingNestedPage: boolean
  isPhoneFactor: boolean
  isTabletFactor: boolean
  isDesktopFactor: boolean
  isSidePanelOpen: boolean
  isNavBarOpen: boolean
  viewportHeight: number | null
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mainContents: {
      display: 'flex',
      flexDirection: 'row',
      minWidth: 0,
      minHeight: 0,
      flexGrow: 1,
    },
    baseContents: (props: StyleProps) =>
      ({
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minWidth: 0,
        overflowY: 'auto',
        backgroundColor: theme.palette.background.default,
      } as any),
    contents: (props: StyleProps) =>
      ({
        marginRight: -SidePanelWidth,
        backgroundColor: theme.palette.background.paper,
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      } as any),
    contentsShift: (props: StyleProps) =>
      ({
        marginRight: 0,
      } as any),
    page: (props: StyleProps) => {
      const styles = {
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        overflowY: 'none',
      } as any

      // if we are on a touchscreen then instead of using 100vh use the actual innerHeight of the window
      // this is due to how mobile browsers will expand and collapse the address bar as you scroll
      if (
        props.viewportHeight !== null &&
        (props.isTabletFactor || props.isPhoneFactor)
      ) {
        styles.height = `${props.viewportHeight}px`
      } else {
        styles.height = '100vh'
      }

      return styles
    },
    appBar: {
      height: AppBarHeight,
      minWidth: 0,
    },
    pageTitle: {
      width: '100vw',
      backgroundColor: theme.palette.neutral.main,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      flexWrap: 'nowrap',
    },
    pageTitleShift: {
      width: `calc(100% - ${SidePanelWidth}px)`,
      marginRight: SidePanelWidth,
    },
    sidebarRightPortal: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      minHeight: 0,
      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
      },
    },
    sidebarRight: (props: StyleProps) => {
      const widthProps = {} as any
      if (props.isPhoneFactor && props.isSidePanelOpen) {
        widthProps.width = '100%'
      }

      return {
        ...widthProps,
      }
    },
    sidebarRightPaper: (props: StyleProps) => {
      const widthProps = {} as any
      if (props.isPhoneFactor) {
        widthProps.width = '100%'
      }

      const heightProps = {} as any
      if (
        props.viewportHeight !== null &&
        (props.isTabletFactor || props.isPhoneFactor)
      ) {
        heightProps.height = `calc(${
          props.viewportHeight
        }px - ${determineAppBarAndAssistBannerHeights(props.user)}px)`
      } else {
        heightProps.height = `calc(100vh - ${determineAppBarAndAssistBannerHeights(
          props.user,
        )}px)`
      }

      return {
        background: theme.palette.background.paper,
        marginTop: determineAppBarAndAssistBannerHeights(props.user),
        ...widthProps,
        ...heightProps,
      } as any
    },
    navContainer: (props: StyleProps) => {
      const styles = {
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        width: props.isNavBarOpen && props.isPhoneFactor ? '100%' : 240,
      } as any

      // if it is a phone then break the nav menu out of the static layout
      // so it slides out on top of the right panel if it is open
      if (props.isPhoneFactor) {
        styles.position = 'fixed'
        styles.marginTop = determineAppBarAndAssistBannerHeights(props.user)
        styles.top = 0
        styles.background = theme.palette.background.paper
        styles.zIndex = 1200
      }

      const heightProps = {} as any
      if (
        props.viewportHeight !== null &&
        (props.isTabletFactor || props.isPhoneFactor)
      ) {
        styles.height = `calc(${
          props.viewportHeight
        }px - ${determineAppBarAndAssistBannerHeights(props.user)}px)`
      } else if (props.isPhoneFactor) {
        styles.height = `calc(100vh - ${determineAppBarAndAssistBannerHeights(
          props.user,
        )}px)`
      }

      return styles
    },
    navContainerPaper: (props: StyleProps) =>
      ({
        backgroundColor: theme.palette.neutral.light,
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        width: props.isNavBarOpen && props.isPhoneFactor ? '100%' : 240,
      } as any),
    hiddenNavContainer: {
      width: '0!important',
    },
    hiddenNavContainerPaper: {
      width: '0!important',
    },
    logoutButton: {
      color: theme.palette.primary.contrastText,
      background: 'none',
      '&:hover': {
        background: 'none',
        color: theme.palette.primary.contrastText,
      },
      textTransform: 'none',
      fontSize: 16,
      fontWeight: 400,
    },
    appVersion: {
      textAlign: 'right',
      padding: theme.spacing(8),
    },
    accessDenied: {
      padding: theme.spacing(8),
      textAlign: 'center',
    },
    snackbarContainer: {
      height: 'unset',
    }
  }),
)

const compareOnSortOrder = (type: PageType) => (
  a: PageDefinition,
  b: PageDefinition,
) => {
  return a.sortOrder(type) - b.sortOrder(type)
}

// these are all routable pages
const pages = Object.values(allPages)

// these are support pages for the left nav
const supportPages = Object.values(allPages)
  .filter((page) => page.type && page.type.includes(PageType.Support))
  .sort(compareOnSortOrder(PageType.Support))

// these are agency pages for the left nav
const agencyPages = Object.values(allPages)
  .filter((page) => page.type && page.type.includes(PageType.Agency))
  .sort(compareOnSortOrder(PageType.Agency))

// these are nested details pages that hide the left nav
const nestedPages = Object.values(allPages)
  .filter((page) => page.type && page.type.includes(PageType.Nested))
  .sort(compareOnSortOrder(PageType.Nested))

// these are admin pages that don't have the left nav
const adminPages = Object.values(allPages)
  .filter((page) => page.type && page.type.includes(PageType.Admin))
  .sort(compareOnSortOrder(PageType.Admin))

export const App = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logError } = useLogger()
  const {
    isAuthenticated,
    user,
    dispatch: dispatchAuthAction,
    performLogoff,
  } = useAuth()
  const { dispatch: dispatchPageAction } = React.useContext(PageContext)
  const {
    state: sidePanelContext,
    dispatch: dispatchSidePanelAction,
  } = useSidePanel()
  const { state: pageState } = useDynamicPageTitle()
  const formFactor = useFormFactor()
  // determine if we can use 100vh or if we have to use a specific pixel height depending on the device
  const viewportHeight = useViewportHeight()

  const {
    error,
    success,
    info,
    warning,
    dispatch: dispatchNotification,
  } = usePageNotifications()

  const { getCurrentSession, logout } = useAuthApi()
  const { stopAssisting } = useCustomerApi()

  const { dispatch: dispatchAgencyAction } = useCurrentAgency(null)
  const { dispatch: dispatchCustomerAction } = React.useContext(
    CurrentCustomerContext,
  )
  const [authorizedPages, setAuthorizedPages] = React.useState<
    PageDefinition[]
  >([])

  const [isNavBarToggledOpen, setIsNavBarToggledOpen] = React.useState<boolean>(
    false,
  )
  
  const statusApi = useStatusApi()
  const [version, setVersion] = React.useState<Version>({ semanticVersion: "N/A", informationalVersion: "N/A" })

  React.useEffect(() => {
    statusApi
    .getVersion()
    .catch(_ => { /* Do nothing */ })
    .then(result => {
      if (result) {
        setVersion(result.data)
      }
    })
  }, [])

  // whenever the App loads on a fresh page load, query WebAdmin for the current session
  // if this is anything other than a success with an auth'd user, then do a client-side logout
  React.useEffect(() => {
    getCurrentSession(dispatchAuthAction)
      .then((response) => {
        if (!response) return

        if (response.status !== 200 || !response.data.isAuthenticated) {
          performLogoff()
        }
      })
      .catch((error) => {
        logError(error)

        performLogoff()
      })
  }, [])

  // re-calculate the authorized page list whenever the auth data changes
  React.useEffect(() => {
    setAuthorizedPages(removeUnauthorizedPages(pages, user))
  }, [user])

  const unassistAgency = () => {
    if (!user || !user.customerAgencyId || !user.licensedAgencyId) return

    stopAssisting(user.customerAgencyId, user.licensedAgencyId)
      .then((response) => {
        if (!response) return

        if (response.status === 200) {
          return getCurrentSession(dispatchAuthAction).then(() => {
            navigate(buildNavLink(user.customerAgencyId as string))

            // now clear out the assist data
            dispatchAgencyAction({
              type: 'Clear',
            })

            dispatchCustomerAction({
              type: 'Clear',
            })
          })
        }

        // TODO
      })
      .catch((error) => {
        //TODO
        logError(error)
      })
  }

  const onExit = () => {
    logout()
      .catch((error) => {
        logError(error)
      })
      .then(() => {
        performLogoff()
      })
  }

  const onReturnToAssistMode = () => {
    if (user === null || !user.isAssisting || agencyPages.length === 0) return

    navigate(agencyPages[0].buildNavLink(user))
  }

  let currentTitle: string | null = null
  let isRenderingAdminPage = false
  let isRenderingAgencyPage = false
  let isRenderingNestedPage = false
  let isRenderingSupportPage = false
  let routeBack: (user: WebAdminUser | null) => string

  if (isAuthenticated) {
    pages.forEach((page: PageDefinition) => {
      try {

      for(let path of page.routes(user))
      {
        const isMatch = doesPageMatchRoute(path, location.pathname)

        if (isMatch) {
          currentTitle =
            typeof page.pageTitle === 'function'
              ? page.pageTitle(user)
              : page.pageTitle
          isRenderingAdminPage =
            isRenderingAdminPage || adminPages.includes(page)
          isRenderingAgencyPage =
            isRenderingAgencyPage || agencyPages.includes(page)
          isRenderingNestedPage =
            isRenderingNestedPage || nestedPages.includes(page)
          isRenderingSupportPage =
            isRenderingSupportPage || supportPages.includes(page)
          
          if(page.routeBack)
            routeBack = page.routeBack

          break
        }
      }
    }
    catch (error) 
    {
      logError(error)
    }
    })
  }

  // determine if the location change is to be considered a top-level page navigation,
  // if so then set the new top level page in the context
  React.useEffect(() => {
    if (user?.isSupport && !isRenderingSupportPage) return

    if (!user?.isSupport && user?.isAdmin && !isRenderingAgencyPage) return

    dispatchPageAction({
      type: 'SetCurrentTopLevelPage',
      payload: {
        pagePathname: location.pathname,
      },
    })
  }, [location, user, isRenderingSupportPage, isRenderingAgencyPage])

  const onBackToSupportHome = () => {
    if (user?.isAssisting && isRenderingAgencyPage) {
      if(user?.customerAgencyId) {
        navigate(buildNavLink(user.customerAgencyId as string))
        return
      }
      else if(user?.isAdmin) {
        navigate(DEFAULT_ADMIN_ROUTE)
        return
      }
    }

    if (routeBack != null) {
      navigate(routeBack(user))
      return
    }

    navigate(DEFAULT_SUPPORT_ROUTE)
  }

  if (pageState.title !== null) {
    currentTitle = pageState.title
  }

  // the UI gets pretty scrunched on tablets with the menu open so we will add the
  // menu button for both tablets and phones
  const shouldEnableNavMenuButton =
    formFactor.isTabletOrSmaller && !isRenderingNestedPage && !isRenderingAdminPage
  const isNavBarHidden = formFactor.isTabletOrSmaller && !isNavBarToggledOpen
  const shouldHideNavBar = isRenderingNestedPage || isRenderingAdminPage || isNavBarHidden

  const classes = useStyles({
    currentTitle,
    user,
    isRenderingNestedPage,
    isPhoneFactor: formFactor.isPhone,
    isTabletFactor: formFactor.isTablet,
    isDesktopFactor: formFactor.isDesktop,
    isSidePanelOpen: sidePanelContext.isRightSidePanelOpen,
    isNavBarOpen: !shouldHideNavBar,
    viewportHeight,
  })

  const appBarTitle = formFactor.isTabletOrSmaller
    ? 'Web Admin'
    : 'Field Ops Agency Administration'

  if (!isAuthenticated) {
    return (
      <React.Fragment>
        <CssBaseline />
        <Box className={classes.page}>
          <AppBar
            title={appBarTitle}
            iconHref="/images/logo.png"
            className={classes.appBar}
          />
          <Box className={classes.baseContents}>
            <Login />
            <Box className={classes.appVersion}>
              <Tooltip
                placement="top"
                arrow
                title={decodeURIComponent(version.informationalVersion)}
                aria-label={decodeURIComponent(version.informationalVersion)}
              >
                <span>{version.semanticVersion}</span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </React.Fragment>
    )
  }

  const navigateTo = (page: PageDefinition) => {
    navigate(page.buildNavLink(user))

    // if the navbar is toggled open on phones then hide it and navigate
    if (isNavBarToggledOpen) setIsNavBarToggledOpen(false)
  }

  const SignOutButton = () => {
    if (formFactor.isPhone) {
      return (
        <IconButton
          className={classes.logoutButton}
          onClick={onExit}
          title="Sign Out"
          aria-label="Sign Out"
          size="large">
          <ExitIcon />
        </IconButton>
      );
    }

    return (
      <Button
        className={classes.logoutButton}
        onClick={onExit}
        title="Sign Out"
        aria-label="Sign Out"
        variant="text"
      >
        Sign Out
      </Button>
    )
  }

  // we are authenticated now, but we need to check to see if the user is an admin user and has an email defined
  if (
    user !== null &&
    user.isAdmin &&
    !user.isSupport &&
    !user.email &&
    user.name !== '#dev-support#'
  ) {
    return (
      <React.Fragment>
        <CssBaseline />
        <Box className={classes.page}>
          <AppBar
            title={appBarTitle}
            iconHref="/images/logo.png"
            className={classes.appBar}
            rightGutter={SignOutButton}
          />
          <Box className={classes.baseContents}>
            <VerifyEmailAddress />
            <Box className={classes.appVersion}>
              <Tooltip
                placement="top"
                arrow
                title={decodeURIComponent(version.informationalVersion)}
                aria-label={decodeURIComponent(version.informationalVersion)}
              >
                <span>{version.semanticVersion}</span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </React.Fragment>
    )
  }

  // if we are logged in as a user that is just a licensed user then show an access denied screen
  // when the page first loads some of these can be undefined, so explicitly check for false
  if (user !== null && user.isSupport === false && user.isAdmin === false) {
    return (
      <React.Fragment>
        <CssBaseline />
        <Box className={classes.page}>
          <AppBar
            title={appBarTitle}
            iconHref="/images/logo.png"
            className={classes.appBar}
            rightGutter={SignOutButton}
          />
          <Box className={classes.mainContents}>
            <Box className={classes.baseContents}>
              <Box className={classes.accessDenied}>
                <Typography variant="h1">Access Denied</Typography>
                <Typography variant="body1">
                  You do not have access to this resource.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </React.Fragment>
    )
  }
 
  // wait until we get authorized pages
  if (authorizedPages.length === 0 && user) {
    return null
  }

  const isRenderingAnAssistPage =
    user !== null && user.isAssisting && isRenderingAgencyPage

  return (
    <React.Fragment>
      <CssBaseline />
      <Box className={classes.page}>
        <AppBar
          title={appBarTitle}
          iconHref="/images/logo.png"
          className={classes.appBar}
          enableMenuButton={shouldEnableNavMenuButton}
          menuIcon={isNavBarToggledOpen ? MuiCloseIcon : MuiMenuIcon}
          onMenuClicked={() => setIsNavBarToggledOpen(!isNavBarToggledOpen)}
          rightGutter={SignOutButton}
        />
        {user?.isAssisting && user.isSupport && (
          <AssistBanner
            onQuitAssistMode={unassistAgency}
            showReturnToAssistButton={!isRenderingAnAssistPage}
            assistedAgencyName={user.licensedAgencyName}
            onReturnToAssistMode={onReturnToAssistMode}
          />
        )}
        <HeaderBar
          title={currentTitle}
          className={clsx(classes.pageTitle, {
            [classes.pageTitleShift]: sidePanelContext.isRightSidePanelOpen,
          })}
          enablePortal
          leftSubtitleContainerId={PAGE_TITLE_SUBTITLE_ID}
          rightGutterPortalContainerId={PAGE_TITLE_RIGHT_ACTIONS_ID}
          centerPortalContainerId={PAGE_TITLE_CENTER_ACTIONS_ID}
          showBackButton={isRenderingAnAssistPage || isRenderingNestedPage}
          onBack={onBackToSupportHome}
        />
        <Box className={classes.mainContents}>
          <NavContainer
            unassistAgency={unassistAgency}
            topLevelPages={supportPages}
            agencyPages={agencyPages}
            navigate={navigateTo}
            currentTitle={currentTitle}
            className={clsx(classes.navContainer, {
              [classes.hiddenNavContainer]: shouldHideNavBar,
            })}
            paperClassName={clsx(classes.navContainerPaper, {
              [classes.hiddenNavContainerPaper]: shouldHideNavBar,
            })}
            open={!shouldHideNavBar}
          />
          <Box
            className={clsx(classes.baseContents, classes.contents, {
              [classes.contentsShift]: sidePanelContext.isRightSidePanelOpen,
            })}
          >
            <ErrorBoundary>
              {/* this is where routes are handled and the pages are rendered */}
              <Routes>

                {authorizedPages.map((page) => {
                  const pageRoutes = page.routes(user)
                  return pageRoutes.map((pageRoute) => {
                    return (
                      <Route
                        key={`Route::${pageRoute}`}
                        path={pageRoute}
                        element={page.component}
                      />
                    )
                  })
                })}
                {user !== null && user.authorizedLicensedAgencies !== null && user.authorizedLicensedAgencies.length > 1 && (
                  <Route
                      path="/"
                      element={<Navigate to={DEFAULT_ADMIN_ROUTE} replace />}
                  />
                )}
                {user !== null && user.isSupport && (
                  <Route
                      path="/"
                      element={<Navigate to={DEFAULT_SUPPORT_ROUTE} replace />}
                  />
                )}
                {user !== null && user.isAdmin && (
                  <Route
                      path="/"
                      element={<Navigate to={DEFAULT_AGENCY_ROUTE} replace />}
                  />
                )}
                <Route path='*' element={<NotFound />} />
              </Routes>
            </ErrorBoundary>
          </Box>
          {/* this is the anchor point for where the SidePanelContent component mounts itself */}
          <SidePanel
            anchor="right"
            open={sidePanelContext.isRightSidePanelOpen}
            className={classes.sidebarRight}
            paperClassName={classes.sidebarRightPaper}
          >
            <Box
              id={SIDE_PANEL_CONTENT_IDS.right}
              className={classes.sidebarRightPortal}
            ></Box>
          </SidePanel>
        </Box>
      </Box>
      <Snackbar className={classes.snackbarContainer}
        severity="error"
        open={error !== null}
        onClose={() => dispatchNotification({ type: 'ClearError' })}
      >
        {error}
      </Snackbar>
      <Snackbar className={classes.snackbarContainer} 
        severity="success"
        open={success !== null}
        onClose={() => dispatchNotification({ type: 'ClearSuccess' })}
      >
        {success}
      </Snackbar>
      <Snackbar className={classes.snackbarContainer}
        severity="warning"
        open={warning !== null}
        onClose={() => dispatchNotification({ type: 'ClearWarning' })}        
      >
        {warning}
      </Snackbar>
      <Snackbar className={classes.snackbarContainer}
        severity="info"
        open={info !== null}
        onClose={() => dispatchNotification({ type: 'ClearInfo' })}
      >
        {info}
      </Snackbar>
      <SessionExpirationWarning />
    </React.Fragment>
  )
}
